import {
  Add,
  Close
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  SelectChangeEvent,
  Stack
} from '@mui/material';
import { IFilterCompanyQueryUtilState } from 'activities/company/CompaniesActivity/types';
import {
  TrendDirectionName,
  TrendName
} from 'generated/graphql';
import { ChatBubble } from 'modules/components/layer02';
import {
  ListedCompanyTrendDropDown,
  TrendDirectionDropDown
} from 'modules/components/layer04';
import React, { useRef } from 'react';
import { theme } from 'theme';
import * as uuid from 'uuid';
import * as ds from './dataService';



export type CompanyFilterProps = {
  form: IFilterCompanyQueryUtilState;
  handleUpdateQueryFilter: (trend: IFilterCompanyQueryUtilState['formState']['trends']) => void;
};

export const FC = (props: CompanyFilterProps) => {
  const slideRef = useRef<HTMLDivElement>(null);
  const {
    formState,
    genFieldProps,
    dispatch
  } = ds.useQuery();

  const handleClose = React.useCallback(
    () => {
      props.form.dispatch({
        type: 'RESET'
      });
    },
    [props.form]
  );

  const handleAddRow = () => {
    dispatch({
      type: 'UPDATE',
      payload: {
        property: 'rows',
        value: [
          ...formState.rows,
          {
            id: uuid.v4(), name: '', direction: ''
          }
        ]
      }
    });
  };

  const handleRemoveRow = (id: string) => {
    const newRows = formState.rows.filter((node) => node.id !== id);
    dispatch({
      type: 'UPDATE',
      payload: {
        property: 'rows',
        value: newRows
      }
    });
    newRows.length === 0 && handleClose();
  };


  const updateSelectField = (e: SelectChangeEvent<unknown> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, rowId: string) => {
    const updatedRows = formState.rows.map((node) => {
      if (node.id === rowId) {
        return {
          ...node,
          name: e.target.value as TrendName
        };
      } else {
        return node;
      }
    });

    dispatch({
      type: 'UPDATE',
      payload: {
        property: 'rows',
        value: updatedRows
      }
    });
  };

  return (
    <Stack
      direction='column'
      columnGap='1rem'
    >
      <ChatBubble.FC
        slideRef={slideRef}
        text='👋 I know uncovering new stocks is important to your process. Use the filters below to find companies that meet the requirements that matter to you. Choose up to 3 filters 🧠.'
        role='SHERLOCK'
      />

      {formState.rows.map((row, i) => (
        <Stack
          ref={slideRef}
          key={i}
          direction='row'
          paddingBottom='1.25rem'
          paddingX='1.5rem'
          alignItems='end'
          columnGap='1rem'
        >
          <ListedCompanyTrendDropDown.FC
            key={`${row.id}-name`}
            value={row.name}
            name={row.name}
            handleChange={(e) => updateSelectField(e, row.id)}
          />
          <TrendDirectionDropDown.FC
            key={`${row.id}-direction`}
            name={row.direction}
            handleChange={(e) => {
              const updatedRows = formState.rows.map((node) => {
                if (node.id === row.id) {
                  return {
                    ...node,
                    direction: e.target.value as TrendDirectionName
                  };
                } else {
                  return node;
                }
              });
              dispatch({
                type: 'UPDATE',
                payload: {
                  property: 'rows',
                  value: updatedRows
                }
              });
            }}
          />

          <Box
            display='flex'
            alignItems='end'
            gap='.5rem'
          >
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              borderRadius='.375rem'
              border={`1px solid ${theme.palette.gray.light20}`}
              height='2rem'
              width='2rem'
            >
              <IconButton
                onClick={() => {
                  handleRemoveRow(row.id);
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
        </Stack>
      ))}
      <Stack
        direction='row'
        justifyContent='space-between'
        paddingRight='1.5rem'
        paddingBottom='1.5rem'
      >
        {formState.rows.length < 3 && (
          <Button
            startIcon={<Add />}
            onClick={() => handleAddRow()}
          >
            Add Filter
          </Button>
        )}

        <div />

        <Button
          sx={{
            maxWidth: '20rem'
          }}
          variant='contained'
          onClick={() => props.handleUpdateQueryFilter(formState.rows)}
        >
          Update Filters
        </Button>
      </Stack>
    </Stack>
  );
};
